import React, { useMemo, useState } from "react";
import {
  CopyIcon,
  CheckIcon,
  TrashIcon,
  ShareIcon,
  EditIcon,
  DotsVerticalIcon,
  LightbulbIcon,
  ImageIcon,
  VideoIcon,
  CalendarCheckIcon,
} from "@/components/icons";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import type { DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { copyToClipboard } from "@/utils/clipboard";
import { BriefListItem, BriefStatus } from "../types";
import { BriefStatusBadge } from "./BriefStatusBadge";
import {
  CATEGORY_COLORS,
  formatDateRange,
} from "@/domains/Home/components/CalendarEventCard";

interface BriefCardProps {
  brief: BriefListItem;
  onEdit?: (brief: BriefListItem) => void;
  onDelete?: (brief: BriefListItem) => void;
  onShare?: (brief: BriefListItem) => void;
  onView?: (brief: BriefListItem) => void;
  onStatusChange?: (brief: BriefListItem, status: BriefStatus) => void;
  isUpdatingStatus?: boolean;
}

export const BriefCard: React.FC<BriefCardProps> = ({
  brief,
  onEdit,
  onDelete,
  onShare,
  onView,
  onStatusChange,
  isUpdatingStatus = false,
}) => {
  const [copiedId, setCopiedId] = useState(false);
  const canEdit = brief.status === "DRAFT";
  const canShare = brief.status === "DRAFT" && brief.ideaCount > 0;
  const canDelete = brief.status === "DRAFT";

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(brief.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const actionItems = useMemo<DropdownMenuEntry[]>(() => {
    const items: DropdownMenuEntry[] = [];
    if (canShare && onShare) {
      items.push({
        id: "share",
        label: "Share",
        icon: <ShareIcon className="w-4 h-4" />,
        onClick: () => onShare(brief),
      });
    }
    if (canEdit && onEdit) {
      items.push({
        id: "edit",
        label: "Edit",
        icon: <EditIcon className="w-4 h-4" />,
        onClick: () => onEdit(brief),
      });
    }
    if (canDelete && onDelete) {
      if (items.length > 0) items.push({ id: "sep", separator: true });
      items.push({
        id: "delete",
        label: "Delete",
        icon: <TrashIcon className="w-4 h-4" />,
        onClick: () => onDelete(brief),
        variant: "danger",
      });
    }
    return items;
  }, [brief, canShare, canEdit, canDelete, onShare, onEdit, onDelete]);

  const events = brief.calendarEvents ?? [];
  const overflow = (brief.calendarEventCount ?? events.length) - events.length;

  return (
    <div
      className="group relative bg-white rounded-2xl hover:shadow-lg transition-all duration-200 cursor-pointer h-[360px] flex flex-col overflow-hidden"
      onClick={() => onView?.(brief)}
    >
      {/* Row 1: Status badge + actions menu */}
      <div className="flex items-center justify-between px-5 pt-5 pb-2">
        <div
          className="flex items-center gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <BriefStatusBadge
            status={brief.status}
            onStatusChange={
              onStatusChange
                ? (status) => onStatusChange(brief, status)
                : undefined
            }
            disabled={isUpdatingStatus}
          />
          <button
            onClick={handleCopyId}
            className={`p-1 rounded-md transition-colors ${
              copiedId
                ? "bg-green-100 text-green-600"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
            }`}
            title={copiedId ? "Copied!" : `Copy ID: ${brief.id}`}
          >
            {copiedId ? (
              <CheckIcon className="w-3.5 h-3.5" />
            ) : (
              <CopyIcon className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        {actionItems.length > 0 && (
          <div
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu
              items={actionItems}
              width="w-36"
              align="right"
              menuClassName="rounded-xl"
              trigger={
                <button
                  aria-label="Brief actions"
                  className="p-1.5 rounded-full text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <DotsVerticalIcon className="w-4 h-4" />
                </button>
              }
            />
          </div>
        )}
      </div>

      {/* Row 2: Meta line */}
      <div className="px-5 pb-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
          {brief.ideaCount} idea{brief.ideaCount === 1 ? "" : "s"}
          {" · "}
          {new Date(brief.createdAt).toLocaleDateString("en-US", {
            month: "long",
            day: "2-digit",
            year: "numeric",
          })}
        </span>
      </div>

      {/* Row 3: Drive folder pills */}
      {(brief.photoDriveUrl ||
        brief.videoDriveUrl ||
        brief.audioDriveUrl ||
        brief.reelsDriveUrl) && (
        <div className="flex flex-wrap items-center gap-1.5 px-5 pb-3">
          {brief.photoDriveUrl && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded">
              <ImageIcon className="w-3 h-3" />
              Photos
            </span>
          )}
          {brief.videoDriveUrl && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded">
              <VideoIcon className="w-3 h-3" />
              Videos
            </span>
          )}
        </div>
      )}

      {/* Row 4: Title */}
      <div className="px-5 pb-2">
        <h3 className="text-2xl font-bold text-gray-900 leading-tight line-clamp-2 group-hover:text-indigo-600 transition-colors">
          {brief.title || "Untitled Brief"}
        </h3>
      </div>

      {/* Row 5: Spacer / context preview */}
      <div className="px-5 flex-1 overflow-hidden">
        {brief.distilledContext && (
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-3">
            {brief.distilledContext}
          </p>
        )}
        {!brief.distilledContext && (
          <div className="flex items-center gap-1.5 text-[11px] text-gray-300">
            <LightbulbIcon className="w-3 h-3" />
            No context yet
          </div>
        )}
      </div>

      {/* Row 6: Calendar events strip */}
      <div className="px-5 pb-3 shrink-0">
        {events.length > 0 ? (
          <div
            className="flex flex-col gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {events.map((event) => {
              const dateLabel = formatDateRange(event);
              const categoryStyle =
                CATEGORY_COLORS[event.category] ||
                "bg-gray-50 border-gray-200 text-gray-800";
              return (
                <div
                  key={event.id}
                  className={`flex items-center gap-2 px-2 py-1 rounded-md border text-[11px] ${categoryStyle}`}
                >
                  {dateLabel && (
                    <span className="font-medium shrink-0">{dateLabel}</span>
                  )}
                  <span className="font-semibold truncate">
                    {event.eventName}
                  </span>
                </div>
              );
            })}
            {overflow > 0 && (
              <span className="text-[10px] text-gray-400 font-medium pl-1">
                +{overflow} more
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-[11px] text-gray-300">
            <CalendarCheckIcon className="w-3 h-3" />
            No events attached
          </div>
        )}
      </div>
    </div>
  );
};
